/* eslint-disable consistent-return */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import moment from 'moment';
import classNames from 'classnames/bind';
import ReCAPTCHA from 'react-google-recaptcha';

import { isEmpty } from 'lodash';
import Button from '../../Button/Button';
import styles from './index.module.scss';
import submitForm from '../../../utils/submitForm';
import { LOCKSMITH_OCCUPATION_ID } from '../../../utils/constants';
import { analyticsKey, recaptchaSiteKey } from '../../../../config';

const STEP = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
  SIX: 6,
};

const cx = classNames.bind(styles);

const Navigation = (props) => {
  const {
    current, getState, setState, next, prev, occupationId, techId
  } = props;
  const enableNext = getState('enableNext', false);
  const address = getState('address');
  const service = getState('service');
  const subCategoryId = getState('subCategoryId');
  const clientPhone = getState('clientPhone');
  const clientName = getState('clientName');
  const isASAP = getState('isASAP');

  const [isNextEnabled, setIsNextEnabled] = useState(false);

  const reCaptchaRef = useRef();

  useEffect(() => {
    if (isASAP !== false) {
      setTimeout(() => {
        setState('isASAP', true);
      }, 1);
    }
  }, []);

  useEffect(() => {
    if (isASAP === false) {
      setState('time', moment());
    }
  }, [isASAP]);

  useEffect(() => {
    if (current === STEP.ONE) {
      if (address && address.zipCode && address.street) {
        setState('enableNext', true);
      } else {
        setState('enableNext', false);
      }
    }

    if (current === STEP.TWO) {
      if (!isEmpty(service) && subCategoryId) {
        setState('enableNext', true);
      } else {
        setState('enableNext', false);
      }
    }

    if (current === STEP.THREE || current === STEP.FOUR) {
      setState('enableNext', true);
    }

    if (current === STEP.FIVE) {
      if (clientName && clientPhone) {
        setState('enableNext', true);
      } else {
        setState('enableNext', false);
      }
    }

    if (occupationId !== LOCKSMITH_OCCUPATION_ID) {
      if (current === STEP.TWO) {
        setState('enableNext', !!service);
      }
    }
  }, [current, address, service, clientPhone, clientName, subCategoryId]);

  useEffect(() => {
    setIsNextEnabled(enableNext);
  }, [enableNext]);

  const sendEvent = () => {
    if (analyticsKey) {
      ReactGA.event({
        category: 'jobCreated',
        action: 'job submitted from microsite',
      });
    }
  };

  const backLabel = current === STEP.SIX ? 'Close' : 'Back';
  const nextLabel = current === STEP.FIVE ? 'Request service' : 'Next';

  const isValid = () => {
    switch (current) {
      case STEP.ONE: {
        return address.zipCode && address.street;
      }
      case STEP.TWO: {
        return !isEmpty(getState('service'));
      }
      case STEP.THREE: {
        return true;
      }
      case STEP.FOUR: {
        return true;
      }
      case STEP.FIVE: {
        const phone = getState('clientPhone', '');
        const isValidPhone = phone.length === 10;

        setState('isPhoneValid', isValidPhone);
        return isValidPhone;
      }
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    const reCaptchaToken = await reCaptchaRef.current.executeAsync();
    const { success, message } = await submitForm(
      getState,
      occupationId,
      address,
      techId,
      reCaptchaToken
    );

    // A token can only be used once, so a failed attempt needs a fresh one.
    reCaptchaRef.current.reset();

    if (success) {
      sendEvent();
      next();
    } else {
      setState('submitErrorMessage', message);
      setState('submitError', true);
    }
  };

  const handleNext = () => {
    if (isValid()) {
      if (current === STEP.FIVE) {
        return handleSubmit();
      }
      next();
    }
  };

  const handlePrev = () => {
    if (current === STEP.SIX) {
      return setState('done', true);
    }

    if (getState('submitError')) {
      setState('submitError', false);
    }
    return prev();
  };

  return (
    <>
      <div className={cx({ navigation: true, singleButton: current === STEP.ONE || current === STEP.SIX })}>
        {current > STEP.ONE
          && <Button variant={'secondary'} onClick={() => handlePrev()}>{backLabel}</Button>}
        {current < STEP.SIX
          && <Button variant={'primary'} onClick={() => handleNext()} disabled={!isNextEnabled}>{nextLabel}</Button>}
      </div>

      <ReCAPTCHA
        ref={reCaptchaRef}
        size="invisible"
        sitekey={recaptchaSiteKey}
      />
    </>
  );
};

Navigation.propTypes = {
  current: PropTypes.number.isRequired,
  getState: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  occupationId: PropTypes.number,
  techId: PropTypes.number,
};

export default Navigation;
